import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEARNING_PATHS,
  PATH_INFO,
  PLACEMENT_QUESTIONS,
  placementResult,
  toLearningPath,
  type LearningPath,
} from '@ready/shared';
import { pathCoverUrl, useApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/lib/me';
import { ShareQuizCard } from '@/components/quiz/share-quiz';
import {
  FindMore,
  PATH_ICONS,
  PathCards,
  PathCover,
  PathEvents,
  PathTimeline,
  RestrictedNote,
} from '@/components/paths';
import { Button, ErrorText, PageHeader } from '@/components/ui';

/** `/paths`: the three learning paths, side by side. */
export function PathsPage() {
  const me = useMe();
  return (
    <div className="space-y-8">
      <PageHeader
        title="Find your starting point"
        description="Three paths through our courses, quizzes and workshops, by how much you already know. Pick the one that sounds like you: you can switch any time."
      />
      <PathCards current={me.data?.learningPath} />
      <PlacementPrompt />
    </div>
  );
}

/** "Not sure?": the link to the placement quiz, under path cards. */
export function PlacementPrompt() {
  return (
    <p className="text-sm text-muted-foreground">
      Not sure which one fits?{' '}
      <Link to="/paths/start" className="-my-2 inline-block py-2 font-semibold text-primary">
        Take the 2-minute quiz
      </Link>
    </p>
  );
}

/** `/paths/start`: "Where should I start?", one question at a time, then a suggestion. */
export function PlacementPage() {
  const api = useApi();
  const { user } = useAuth();
  const me = useMe();
  const queryClient = useQueryClient();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [index, setIndex] = useState(0);
  const save = useMutation({
    mutationFn: (p: LearningPath) => api.setPath(p),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['myPath'] });
    },
  });
  const total = PLACEMENT_QUESTIONS.length;
  const done = index >= total;

  if (done) {
    const path = placementResult(answers);
    const info = PATH_INFO[path];
    const Icon = PATH_ICONS[path];
    const saved = me.data?.learningPath === path;
    return (
      <div className="max-w-3xl space-y-6">
        <PageHeader title="Where should I start?" />
        <div className="space-y-4 overflow-hidden rounded-2xl border border-primary bg-card p-6">
          <PathCover
            path={path}
            className="-mx-6 -mt-6 w-[calc(100%+3rem)] max-w-none border-b border-border"
          />
          <p className="text-sm font-semibold text-muted-foreground">We suggest</p>
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-muted">
              <Icon className="h-6 w-6 text-primary" aria-hidden />
            </span>
            <div>
              <h2 className="font-display text-2xl font-semibold">{info.name}</h2>
              <p className="text-sm text-muted-foreground">{info.tagline}</p>
            </div>
          </div>
          <p className="leading-relaxed">{info.definition}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {user ? (
              saved ? (
                <span className="text-sm font-semibold text-primary">Saved as your path.</span>
              ) : (
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => save.mutate(path)}
                  disabled={save.isPending}
                >
                  Make this my path
                </Button>
              )
            ) : (
              <Link
                to="/signin"
                className="inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground sm:w-auto"
              >
                Sign in to save it
              </Link>
            )}
            <Link to={`/paths/${path}`} className="-my-2 py-2 text-sm font-semibold text-primary">
              See the path, step by step
            </Link>
          </div>
          <ErrorText error={save.error} />
        </div>
        <p className="text-sm text-muted-foreground">
          It’s only a suggestion: any path is open to you.{' '}
          <Link to="/paths" className="-my-2 inline-block py-2 font-semibold text-primary">
            Compare all three
          </Link>{' '}
          or{' '}
          <button
            type="button"
            className="-my-2 py-2 font-semibold text-primary"
            onClick={() => {
              setAnswers({});
              setIndex(0);
            }}
          >
            start over
          </button>
          .
        </p>
      </div>
    );
  }

  const question = PLACEMENT_QUESTIONS[index]!;
  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        title="Where should I start?"
        description="Eight quick questions, no wrong answers. We’ll suggest a learning path."
      />
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Question {index + 1} of {total}
        </p>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div className={`h-full bg-primary transition-all ${PROGRESS_WIDTHS[index] ?? 'w-0'}`} />
        </div>
        <h2 className="font-display text-xl font-semibold">{question.prompt}</h2>
        <div className="grid gap-3" role="radiogroup" aria-label={question.prompt}>
          {question.options.map((option, i) => (
            <button
              key={option.label}
              type="button"
              role="radio"
              aria-checked={answers[question.id] === i}
              className={`rounded-xl border p-4 text-left transition-colors hover:border-primary ${
                answers[question.id] === i ? 'border-primary bg-primary/5' : 'border-border bg-card'
              }`}
              onClick={() => {
                setAnswers((a) => ({ ...a, [question.id]: i }));
                setIndex((n) => n + 1);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        {index > 0 && (
          <button
            type="button"
            className="-my-2 py-2 text-sm text-muted-foreground underline underline-offset-2"
            onClick={() => setIndex((n) => n - 1)}
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
}

/** Progress bar widths for eight questions (CSP forbids inline styles). */
const PROGRESS_WIDTHS = ['w-1/8', 'w-2/8', 'w-3/8', 'w-4/8', 'w-5/8', 'w-6/8', 'w-7/8', 'w-full'];

/** `/paths/:path`: one path as a timeline, its events, and "Find more". */
export function PathPage() {
  const { path: raw } = useParams();
  const path = toLearningPath(raw);
  if (!path) return <Navigate to="/paths" replace />;
  // Old ids (deep-end) move to the renamed path's address.
  if (path !== raw) return <Navigate to={`/paths/${path}`} replace />;
  return <PathDetail key={path} path={path} />;
}

function PathDetail({ path }: { path: LearningPath }) {
  const api = useApi();
  const { user } = useAuth();
  const me = useMe();
  const queryClient = useQueryClient();
  const info = PATH_INFO[path];
  const Icon = PATH_ICONS[path];

  const publicData = useQuery({
    queryKey: ['publicPath', path],
    queryFn: () => api.publicPath(path),
  });
  const mine = useQuery({
    queryKey: ['myPath', path],
    queryFn: () => api.myPath(path),
    enabled: Boolean(user),
  });
  const choose = useMutation({
    mutationFn: (p: LearningPath | null) => api.setPath(p),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['myPath'] });
    },
  });
  const isMine = me.data?.learningPath === path;
  const items = user ? mine.data?.items : publicData.data?.items;

  return (
    <div className="space-y-8">
      <Link
        to="/paths"
        className="-my-2 inline-block py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        ← All learning paths
      </Link>

      <img
        src={pathCoverUrl(path)}
        alt=""
        className="aspect-[2/1] w-full max-w-3xl rounded-[var(--radius)] border border-border object-cover"
      />

      <header className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-muted">
            <Icon className="h-6 w-6 text-primary" aria-hidden />
          </span>
          <div>
            <p className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Learning path
            </p>
            <h1 className="font-display text-2xl font-semibold sm:text-3xl">{info.name}</h1>
          </div>
        </div>
        <p className="text-lg font-medium">{info.tagline}</p>
        <p className="max-w-3xl leading-relaxed text-muted-foreground">{info.definition}</p>
        <p className="max-w-3xl rounded-lg bg-muted/60 p-3 text-sm leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">This is you if… </span>“{info.example}”
        </p>
        {user ? (
          isMine ? (
            <p className="text-sm">
              <span className="font-semibold text-primary">This is your path.</span>{' '}
              <button
                type="button"
                className="-my-2 py-2 text-muted-foreground underline underline-offset-2"
                onClick={() => choose.mutate(null)}
                disabled={choose.isPending}
              >
                Clear my choice
              </button>
            </p>
          ) : (
            <Button
              className="w-full sm:w-auto"
              onClick={() => choose.mutate(path)}
              disabled={choose.isPending}
            >
              Make this my path
            </Button>
          )
        ) : (
          <p className="text-sm text-muted-foreground">
            <Link to="/signin" className="-my-2 py-2 font-semibold text-primary">
              Sign in
            </Link>{' '}
            to follow this path and track your progress. Everything is free.
          </p>
        )}
        <ErrorText error={choose.error} />
      </header>

      <section className="space-y-4">
        <h2 className="font-display text-xl font-semibold">The path, step by step</h2>
        {items ? (
          items.length ? (
            <PathTimeline path={path} items={items} next={mine.data?.next} />
          ) : (
            <p className="text-sm text-muted-foreground">Nothing published on this path yet.</p>
          )
        ) : (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        <ErrorText error={user ? mine.error : publicData.error} />
        {info.note && <p className="text-sm text-muted-foreground">{info.note}</p>}
        <RestrictedNote path={path} />
        <FindMore path={path} />
      </section>

      <ShareQuizCard
        kind="path"
        slug={path}
        title={info.name}
        audience="public"
        heading="Know someone this path fits?"
        text="Share it on LinkedIn, send the link, or print a poster with a QR code."
      />

      <section className="space-y-4">
        <h2 className="font-display text-xl font-semibold">Upcoming workshops and webinars</h2>
        {publicData.data ? <PathEvents events={publicData.data.events} /> : null}
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-xl font-semibold">Other paths</h2>
        <ul className="flex flex-col gap-2 sm:flex-row sm:gap-6">
          {LEARNING_PATHS.filter((p) => p !== path).map((p) => (
            <li key={p}>
              <Link
                to={`/paths/${p}`}
                className="-my-2 inline-block py-2 font-semibold text-primary"
              >
                {PATH_INFO[p].name}: {PATH_INFO[p].tagline}
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
